/** Inquiry history — shared builder for terminal assistant (mirrors main app timeline). */
import { STATUS_LABELS, fmtDateTime } from './utils.js';
import { formatStageHistoryDescription, statusHistoryTitle } from './status-stage.js';

const HISTORY_AUDIT_LABELS = {
  'lead.create': 'Inquiry created',
  'lead.update': 'Inquiry updated',
  'lead.status': 'Status changed',
  'lead.call': 'Call logged',
  'lead.delete': 'Moved to recycle bin',
  'lead.request_edit': 'Edit access requested',
  'lead.takeover': 'Inquiry taken over',
  'lead.grant_edit': 'Edit access granted',
  'lead.deny_edit': 'Edit access denied',
};

function parseAuditDetail(detailRaw) {
  try {
    return typeof detailRaw === 'string' ? JSON.parse(detailRaw || '{}') : (detailRaw || {});
  } catch {
    return {};
  }
}

function isAutoStatusNote(text) {
  if (!text) return false;
  const prefixes = [
    ...Object.values(STATUS_LABELS).map((l) => `${l}: `),
    'Client not interested: ',
    'Reopened: ',
  ];
  return prefixes.some((p) => text.startsWith(p));
}

function extractStatusNoteExtra(text, status) {
  if (!text) return '';
  const label = STATUS_LABELS[status] || status;
  const prefixes = [`${label}: `, 'Client not interested: ', 'Reopened: '];
  for (const p of prefixes) {
    if (text.startsWith(p)) {
      const rest = text.slice(p.length).trim();
      if (!rest || rest === label || rest === STATUS_LABELS[status]) return '';
      return rest;
    }
  }
  return '';
}

function formatAuditHistoryLine(action, detailRaw, lead) {
  const detail = parseAuditDetail(detailRaw);
  if (action === 'lead.status' && detail.status) {
    return statusHistoryTitle(lead?.type || 'tour', detail.status, detail.from);
  }
  return HISTORY_AUDIT_LABELS[action] || action.replace(/^lead\./, '').replace(/_/g, ' ');
}

function statusHistoryDescription(auditDetail, lead, recordedAt) {
  const status = auditDetail.status;
  if (!status || !lead) return '';
  const stageData = auditDetail.stage_data
    ?? lead.extra?.stage_data?.[status]
    ?? {};
  let text = formatStageHistoryDescription(lead.type, status, stageData, lead, recordedAt);
  const noteExtra = extractStatusNoteExtra(auditDetail.note_extra, status);
  if (noteExtra) text = text ? `${text}\nNotes: ${noteExtra}` : `Notes: ${noteExtra}`;
  if (!text) text = '';
  return text;
}

function findLinkedStatusNote(notes, status, auditTime) {
  const label = STATUS_LABELS[status];
  if (!label) return null;
  const prefix = `${label}: `;
  let best = null;
  let bestDelta = Infinity;
  for (const n of notes || []) {
    if (!n.text?.startsWith(prefix)) continue;
    const delta = Math.abs(new Date(n.created_at).getTime() - auditTime);
    if (delta < bestDelta && delta < 120000) {
      best = n;
      bestDelta = delta;
    }
  }
  return best;
}

function activitySortKey(item) {
  if (item.kind === 'note' || String(item.time || '').includes('T')) {
    const t = new Date(item.time).getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  const parts = String(item.time || '').trim().split(/\s+/);
  const d = parts[0];
  const tm = parts[1] || '00:00';
  const t = new Date(`${d}T${tm}:00+05:30`).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function buildHistoryItems(history) {
  const items = [];
  const l = history.lead;
  const audits = history.audit_events || [];

  const hasCreateAudit = audits.some((a) => a.action === 'lead.create');
  if (l?.created_at && !hasCreateAudit) {
    items.push({
      kind: 'event',
      time: l.created_at,
      title: 'Inquiry created',
      text: `${l.type || 'inquiry'} · ${l.package_name || l.origin || '—'}`,
      who: l.agent_name || 'Agent',
    });
  }

  audits.forEach((a) => {
    const detail = parseAuditDetail(a.detail);
    let text = '';
    let title = formatAuditHistoryLine(a.action, a.detail, l);
    let kind = 'audit';

    if (a.action === 'lead.create') {
      text = `${l.type || 'inquiry'} · ${l.package_name || l.origin || '—'}`;
    } else if (a.action === 'lead.status' && detail.status) {
      kind = 'status';
      title = formatAuditHistoryLine(a.action, a.detail, l);
      const auditTime = new Date(a.created_at).getTime();
      const linkedNote = findLinkedStatusNote(history.notes, detail.status, auditTime);
      if (linkedNote) detail.note_extra = linkedNote.text;
      text = statusHistoryDescription(detail, l, a.created_at);
    }

    items.push({
      kind,
      time: a.created_at,
      title,
      text,
      who: a.actor_name || 'System',
      status: detail.status || null,
    });
  });

  (history.notes || []).forEach((n) => {
    if (isAutoStatusNote(n.text)) return;
    const isPricing = String(n.text || '').startsWith('Pricing updated:');
    items.push({
      kind: isPricing ? 'pricing' : 'note',
      time: n.created_at,
      title: isPricing ? 'Pricing updated' : 'Note',
      text: n.text,
      who: n.author_name || 'Agent',
    });
  });

  (history.call_logs || []).forEach((c) => {
    const parts = [];
    if (c.response) parts.push(`Via: ${c.response}`);
    if (c.notes) parts.push(`Discussion: ${c.notes}`);
    items.push({
      kind: 'call',
      time: `${c.call_date} ${c.call_time || ''}`.trim(),
      title: 'Call log',
      text: parts.join(' · ') || '',
      who: c.agent_name || 'Agent',
    });
  });

  items.sort((a, b) => activitySortKey(b) - activitySortKey(a));
  return items;
}

export function formatHistoryTime(time) {
  if (String(time || '').includes('T')) return fmtDateTime(time) || time;
  const parts = String(time || '').trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0]} ${parts[1]}`;
  return fmtDateTime(time) || time || '—';
}

/** Plain-text timeline for the terminal transcript. */
export function formatHistoryForTerminal(history) {
  const items = buildHistoryItems(history);
  if (!items.length) return 'No history yet.';

  const lines = [`Inquiry history (${items.length} ${items.length === 1 ? 'entry' : 'entries'})`, ''];
  items.forEach((item, i) => {
    lines.push(`${formatHistoryTime(item.time)} · ${item.title}`);
    lines.push(`  ${item.who || 'Agent'}`);
    if (item.text) {
      item.text.split('\n').forEach((ln) => {
        if (ln.trim()) lines.push(`  ${ln.trim()}`);
      });
    }
    if (i < items.length - 1) lines.push('');
  });
  return lines.join('\n');
}
